import React, { useMemo } from 'react';
import { useHistory, useTranslation } from '@shared/core';
import { useEventInfo } from '@shared/utils/eventInfo';
import { numberToWord } from '@withjoy/joykit/components/ColumnsV1/utils/numberToWord';
import { capitalizeWord } from '@shared/utils/strings';
import { Info } from '@withjoy/joykit/icons';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { Flex, LinkV2, TextV2 } from '@withjoy/joykit';

interface NonJoyBookingsWarningProps {
  nonJoyHotelsCount: number;
}

export const NonJoyBookingsWarning = ({ nonJoyHotelsCount }: NonJoyBookingsWarningProps) => {
  const { tq } = useTranslation('websiteEditor');
  const { eventHandle } = useEventInfo();
  const numberOfHotels = useMemo(() => {
    const result = numberToWord(nonJoyHotelsCount);
    return typeof result === 'number' ? result : capitalizeWord(result);
  }, [nonJoyHotelsCount]);

  const history = useHistory();

  const handleSwapToJoyHotels = useEventCallback(() => {
    history.push({
      pathname: `/${eventHandle}/edit/accommodations`,
      state: { freeze: true },
    });
  });

  return (
    <Flex
      width="100%"
      gap={6}
      padding={6}
      border="1px solid"
      borderColor="info5"
      borderRadius="8px"
    >
      <Info
        size={32}
        color="info5"
      />
      <Flex
        flexDirection="column"
        gap={6}
        justifyContent="flex-start"
      >
        <TextV2
          textAlign="left"
          typographyVariant="body1"
        >
          {tq`You’re missing booking insights for ${numberOfHotels} manually added hotels. Add Joy hotels to see who books, track engagement, and make the most of every stay.`}
        </TextV2>
        <LinkV2
          as="button"
          textAlign="left"
          onClick={handleSwapToJoyHotels}
          color="mono10"
        >
          <TextV2 typographyVariant="body1">{tq`Swap to Joy Hotels`}</TextV2>
        </LinkV2>
      </Flex>
    </Flex>
  );
};

NonJoyBookingsWarning.displayName = 'NonJoyBookingsWarning';
